/**
 * storage-namespace-migration-logic.js — Lógica pura de migración de keys.
 *
 * Aislada en archivo separado para que sea cargable desde Node (tests) sin
 * depender de `import.meta.env`. El módulo principal storage-namespace.js
 * importa estas funciones y les pasa `localStorage` + `STORAGE_PREFIX` reales.
 *
 * Diseño:
 *   - Funciones puras que reciben `storage` (cualquier objeto Storage-like) y
 *     `prefijoBrandActual` como parámetros.
 *   - Sin top-level side effects.
 *   - Compatible con ESM en Node y con bundles Vite.
 */

const PREFIJO_BRAND_QUE_DEBE_MIGRAR = 'loterianacional';
const SUFIJO_FLAG_DE_MIGRACION_COMPLETADA = '_storage_namespace_migrated_v1';

const PREFIJO_LEGACY_GUION_BAJO = 'loto37_';
const PREFIJO_LEGACY_GUION = 'loto37-';
const KEY_LEGACY_ESPECIAL_SKELETON_SIN_PREFIJO_37 = 'loto_skeleton_snapshots';

/**
 * Mapea una key legacy (loto37_*, loto37-*, loto_skeleton_snapshots) al equivalente
 * con el prefijo de brand actual. Devuelve null si la key no necesita migración.
 *
 * Pura — no toca storage.
 */
export function traducirKeyDeStoragePrefijoLoto37(keyLegacy, prefijoBrandActual) {
    if (typeof keyLegacy !== 'string' || keyLegacy.length === 0) return null;

    if (keyLegacy.startsWith(PREFIJO_LEGACY_GUION_BAJO)) {
        // loto37_X → <prefijo>_X
        return prefijoBrandActual + keyLegacy.slice('loto37'.length);
    }
    if (keyLegacy.startsWith(PREFIJO_LEGACY_GUION)) {
        // loto37-X → <prefijo>-X
        return prefijoBrandActual + keyLegacy.slice('loto37'.length);
    }
    if (keyLegacy === KEY_LEGACY_ESPECIAL_SKELETON_SIN_PREFIJO_37) {
        return prefijoBrandActual + '_skeleton_snapshots';
    }
    return null;
}

/**
 * Ejecuta la migración de keys legacy al prefijo de brand actual sobre el
 * `storage` recibido (Storage-like API: getItem/setItem/removeItem/keys).
 *
 * Reglas:
 *   - Solo migra si prefijoBrandActual === 'loterianacional'. Otras brands
 *     nacen con prefijo nuevo, no hay datos viejos.
 *   - Idempotente: una flag '<prefix>_storage_namespace_migrated_v1=1' se setea
 *     al final. Si ya existe, no-op.
 *   - Las keys legacy NO se borran (rollback safety).
 *   - Si una key destino ya tiene valor distinto, NO se sobrescribe (prioridad
 *     a escritura más reciente del código nuevo).
 *
 * Devuelve un objeto con stats: { migradas, saltadas, totalLegacy }.
 * Útil para tests y telemetría.
 */
export function ejecutarMigracionDeKeysSiCorresponde(storage, prefijoBrandActual) {
    const stats = { migradas: 0, saltadas: 0, totalLegacy: 0, ejecuto: false };

    if (prefijoBrandActual !== PREFIJO_BRAND_QUE_DEBE_MIGRAR) return stats;
    if (!storage || typeof storage.getItem !== 'function') return stats;

    const flagKey = prefijoBrandActual + SUFIJO_FLAG_DE_MIGRACION_COMPLETADA;
    if (storage.getItem(flagKey) === '1') return stats;

    stats.ejecuto = true;

    // Snapshot de keys originales — ANTES de mutar, para no infinite-loopear
    // ni iterar sobre las keys que vamos creando.
    const keysOriginales = listarKeysDelStorage(storage);

    for (const keyLegacy of keysOriginales) {
        const claveNueva = traducirKeyDeStoragePrefijoLoto37(keyLegacy, prefijoBrandActual);

        if (!claveNueva || claveNueva === keyLegacy) continue;

        stats.totalLegacy++;

        // Idempotencia: si ya existe en destino, no sobrescribir.
        if (storage.getItem(claveNueva) !== null) {
            stats.saltadas++;
            continue;
        }

        const valor = storage.getItem(keyLegacy);
        if (valor === null) {
            stats.saltadas++;
            continue;
        }

        storage.setItem(claveNueva, valor);
        stats.migradas++;
    }

    storage.setItem(flagKey, '1');
    return stats;
}

/**
 * Helper para listar keys del storage. localStorage real expone Object.keys()
 * y `length`+`key(i)`. Aceptamos ambos para compatibilidad con mocks.
 */
function listarKeysDelStorage(storage) {
    if (typeof storage.length === 'number' && typeof storage.key === 'function') {
        const keys = [];
        for (let i = 0; i < storage.length; i++) {
            const k = storage.key(i);
            if (k !== null) keys.push(k);
        }
        return keys;
    }
    return Object.keys(storage);
}
